import { useCallback, useMemo, useState } from "react";
import type { PriceListRow } from "./price-list-row";

export type RevenueDepartmentOption = {
  id: string;
  name: string;
};

export type PriceEditFields = {
  price?: number;
  active?: boolean;
  revenueDepartment?: RevenueDepartmentOption;
};

export type PendingPriceEdit = {
  row: PriceListRow;
} & PriceEditFields;

type PendingMap = Map<string, PendingPriceEdit>;

// Value-aware: a field counts as "changed" only when its pending value
// actually differs from the original row value. Lets the row blue tint
// flip on the first keystroke that produces a different value (and back
// off if the user types the original value back in).
const hasFieldChange = (edit: PendingPriceEdit) => {
  const r = edit.row;
  if (edit.price !== undefined && edit.price !== r.price) return true;
  if (edit.active !== undefined && edit.active !== r.active) return true;
  if (
    edit.revenueDepartment !== undefined &&
    edit.revenueDepartment.id !== r.revenueDepartment.id
  )
    return true;
  return false;
};

// Effective row = baseline merged with any pending field overrides. Used
// for rendering the row's current value and as the input to percentage
// adjustments so they stack against the live value, not the baseline.
const computeEffectiveRow = (edit: PendingPriceEdit): PriceListRow => ({
  ...edit.row,
  price: edit.price ?? edit.row.price,
  active: edit.active ?? edit.row.active,
  revenueDepartment: edit.revenueDepartment ?? edit.row.revenueDepartment,
});

export type PendingPriceEdits = {
  pending: PendingMap;
  selectedIds: Set<string>;
  changedCount: number;
  isSelected: (rowId: string) => boolean;
  isChanged: (rowId: string) => boolean;
  effectiveRow: (row: PriceListRow) => PriceListRow;
  toggleSelected: (row: PriceListRow) => void;
  setSelected: (row: PriceListRow, selected: boolean) => void;
  stageEdit: (row: PriceListRow, partial: PriceEditFields) => void;
  applyAdjustment: (
    transform: (row: PriceListRow) => Partial<PriceEditFields>,
  ) => void;
  reset: () => void;
};

export function usePendingPriceEdits(): PendingPriceEdits {
  const [pending, setPending] = useState<PendingMap>(() => new Map());

  const selectedIds = useMemo(() => new Set(pending.keys()), [pending]);

  const changedCount = useMemo(() => {
    let n = 0;
    for (const edit of pending.values()) {
      if (hasFieldChange(edit)) n += 1;
    }
    return n;
  }, [pending]);

  const isSelected = useCallback(
    (rowId: string) => pending.has(rowId),
    [pending],
  );

  const isChanged = useCallback(
    (rowId: string) => {
      const edit = pending.get(rowId);
      return !!edit && hasFieldChange(edit);
    },
    [pending],
  );

  const effectiveRow = useCallback(
    (row: PriceListRow): PriceListRow => {
      const edit = pending.get(row.id);
      return edit ? computeEffectiveRow(edit) : row;
    },
    [pending],
  );

  const setSelected = useCallback((row: PriceListRow, selected: boolean) => {
    setPending((prev) => {
      const isCurrentlySelected = prev.has(row.id);
      if (selected === isCurrentlySelected) return prev;
      const next = new Map(prev);
      if (selected) {
        next.set(row.id, { row });
      } else {
        next.delete(row.id);
      }
      return next;
    });
  }, []);

  const toggleSelected = useCallback(
    (row: PriceListRow) => {
      setSelected(row, !pending.has(row.id));
    },
    [pending, setSelected],
  );

  const stageEdit = useCallback(
    (row: PriceListRow, partial: PriceEditFields) => {
      setPending((prev) => {
        const next = new Map(prev);
        const existing = next.get(row.id);
        // The table feeds back the *effective* row (baseline + pending) on
        // every keystroke. Pin the baseline to whatever was captured on the
        // first stage so change detection always compares against the
        // originally-fetched values — without this, typing the original
        // value back in would still register as a change.
        const baseline = existing?.row ?? row;
        next.set(row.id, { ...existing, ...partial, row: baseline });
        return next;
      });
    },
    [],
  );

  const applyAdjustment = useCallback(
    (transform: (row: PriceListRow) => Partial<PriceEditFields>) => {
      setPending((prev) => {
        if (prev.size === 0) return prev;
        const next = new Map(prev);
        for (const [id, edit] of prev.entries()) {
          // Stack adjustments on top of the current effective value, not
          // the baseline — applying +10% twice should compound to +21%.
          // edit.row stays untouched so change detection still compares
          // against the original.
          next.set(id, { ...edit, ...transform(computeEffectiveRow(edit)) });
        }
        return next;
      });
    },
    [],
  );

  const reset = useCallback(() => {
    setPending(new Map());
  }, []);

  return {
    pending,
    selectedIds,
    changedCount,
    isSelected,
    isChanged,
    effectiveRow,
    toggleSelected,
    setSelected,
    stageEdit,
    applyAdjustment,
    reset,
  };
}
